import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import templateService from 'services/TemplateService';

export const initialState = {
  loading: false,
  templatesEmail: [],
  templatesPdf: [],
  variaveis: [],
  currentTemplate: null,
  previewHtml: null,
  previewPdfUrl: null,
  error: null,
  message: '',
  showMessage: false
};

// ==================== TEMPLATES DE EMAIL ====================

export const fetchTemplatesEmail = createAsyncThunk(
  'templates/fetchEmail',
  async (_, { rejectWithValue }) => {
    try {
      const response = await templateService.getTemplatesEmail();
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Erro ao carregar templates de email');
    }
  }
);

export const createTemplateEmail = createAsyncThunk(
  'templates/createEmail',
  async (template, { rejectWithValue }) => {
    try {
      const response = await templateService.criarTemplateEmail(template);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Erro ao criar template de email');
    }
  }
);

export const updateTemplateEmail = createAsyncThunk(
  'templates/updateEmail',
  async ({ id, template }, { rejectWithValue }) => {
    try {
      const response = await templateService.atualizarTemplateEmail(id, template);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Erro ao atualizar template de email');
    }
  }
);

export const deleteTemplateEmail = createAsyncThunk(
  'templates/deleteEmail',
  async (id, { rejectWithValue }) => {
    try {
      await templateService.excluirTemplateEmail(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Erro ao excluir template de email');
    }
  }
);

export const previewTemplateEmail = createAsyncThunk(
  'templates/previewEmail',
  async ({ id, variaveis = {} }, { rejectWithValue }) => {
    try {
      const response = await templateService.previewTemplateEmail(id, variaveis);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Erro ao gerar preview');
    }
  }
);

export const duplicateTemplateEmail = createAsyncThunk(
  'templates/duplicateEmail',
  async ({ id, novoNome }, { rejectWithValue }) => {
    try {
      const response = await templateService.duplicarTemplateEmail(id, novoNome);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Erro ao duplicar template');
    }
  }
);

// ==================== TEMPLATES DE PDF ====================

export const fetchTemplatesPdf = createAsyncThunk(
  'templates/fetchPdf',
  async (_, { rejectWithValue }) => {
    try {
      const response = await templateService.getTemplatesPdf();
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Erro ao carregar templates de PDF');
    }
  }
);

export const createTemplatePdf = createAsyncThunk(
  'templates/createPdf',
  async (template, { rejectWithValue }) => {
    try {
      const response = await templateService.criarTemplatePdf(template);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Erro ao criar template de PDF');
    }
  }
);

export const updateTemplatePdf = createAsyncThunk(
  'templates/updatePdf',
  async ({ id, template }, { rejectWithValue }) => {
    try {
      const response = await templateService.atualizarTemplatePdf(id, template);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Erro ao atualizar template de PDF');
    }
  }
);

export const deleteTemplatePdf = createAsyncThunk(
  'templates/deletePdf',
  async (id, { rejectWithValue }) => {
    try {
      await templateService.excluirTemplatePdf(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Erro ao excluir template de PDF');
    }
  }
);

export const previewTemplatePdf = createAsyncThunk(
  'templates/previewPdf',
  async ({ id, variaveis = {} }, { rejectWithValue }) => {
    try {
      const response = await templateService.previewTemplatePdf(id, variaveis);
      const url = URL.createObjectURL(new Blob([response], { type: 'application/pdf' }));
      return url;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Erro ao gerar preview');
    }
  }
);

export const duplicateTemplatePdf = createAsyncThunk(
  'templates/duplicatePdf',
  async ({ id, novoNome }, { rejectWithValue }) => {
    try {
      const response = await templateService.duplicarTemplatePdf(id, novoNome);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Erro ao duplicar template');
    }
  }
);

// ==================== VARIAVEIS ====================

export const fetchVariaveis = createAsyncThunk(
  'templates/fetchVariaveis',
  async (contexto = null, { rejectWithValue }) => {
    try {
      const response = await templateService.getVariaveis(contexto);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Erro ao carregar variáveis');
    }
  }
);

// ==================== SLICE ====================

export const templateSlice = createSlice({
  name: 'templates',
  initialState,
  reducers: {
    setCurrentTemplate: (state, action) => {
      state.currentTemplate = action.payload;
    },
    clearCurrentTemplate: (state) => {
      state.currentTemplate = null;
    },
    clearPreview: (state) => {
      state.previewHtml = null;
      if (state.previewPdfUrl) {
        URL.revokeObjectURL(state.previewPdfUrl);
      }
      state.previewPdfUrl = null;
    },
    clearError: (state) => {
      state.error = null;
      state.message = '';
      state.showMessage = false;
    },
    showTemplateMessage: (state, action) => {
      state.message = action.payload;
      state.showMessage = true;
    },
    hideTemplateMessage: (state) => {
      state.message = '';
      state.showMessage = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Email Templates
      .addCase(fetchTemplatesEmail.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTemplatesEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.templatesEmail = action.payload;
      })
      .addCase(fetchTemplatesEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Email Template
      .addCase(createTemplateEmail.pending, (state) => {
        state.loading = true;
      })
      .addCase(createTemplateEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.templatesEmail.push(action.payload);
        state.currentTemplate = action.payload;
        state.message = 'Template criado com sucesso!';
        state.showMessage = true;
      })
      .addCase(createTemplateEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Email Template
      .addCase(updateTemplateEmail.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.templatesEmail.findIndex(t => t.id === action.payload.id);
        if (index >= 0) {
          state.templatesEmail[index] = action.payload;
        }
        state.currentTemplate = action.payload;
        state.message = 'Template atualizado com sucesso!';
        state.showMessage = true;
      })
      // Delete Email Template
      .addCase(deleteTemplateEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.templatesEmail = state.templatesEmail.filter(t => t.id !== action.payload);
        state.message = 'Template excluído com sucesso!';
        state.showMessage = true;
      })
      // Preview Email
      .addCase(previewTemplateEmail.fulfilled, (state, action) => {
        state.previewHtml = action.payload;
      })
      // Duplicate Email
      .addCase(duplicateTemplateEmail.fulfilled, (state, action) => {
        state.templatesEmail.push(action.payload);
        state.message = 'Template duplicado com sucesso!';
        state.showMessage = true;
      })
      // Fetch PDF Templates
      .addCase(fetchTemplatesPdf.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTemplatesPdf.fulfilled, (state, action) => {
        state.loading = false;
        state.templatesPdf = action.payload;
      })
      .addCase(fetchTemplatesPdf.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create PDF Template
      .addCase(createTemplatePdf.pending, (state) => {
        state.loading = true;
      })
      .addCase(createTemplatePdf.fulfilled, (state, action) => {
        state.loading = false;
        state.templatesPdf.push(action.payload);
        state.currentTemplate = action.payload;
        state.message = 'Template criado com sucesso!';
        state.showMessage = true;
      })
      .addCase(createTemplatePdf.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update PDF Template
      .addCase(updateTemplatePdf.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.templatesPdf.findIndex(t => t.id === action.payload.id);
        if (index >= 0) {
          state.templatesPdf[index] = action.payload;
        }
        state.currentTemplate = action.payload;
        state.message = 'Template atualizado com sucesso!';
        state.showMessage = true;
      })
      // Delete PDF Template
      .addCase(deleteTemplatePdf.fulfilled, (state, action) => {
        state.loading = false;
        state.templatesPdf = state.templatesPdf.filter(t => t.id !== action.payload);
        state.message = 'Template excluído com sucesso!';
        state.showMessage = true;
      })
      // Preview PDF
      .addCase(previewTemplatePdf.fulfilled, (state, action) => {
        if (state.previewPdfUrl) {
          URL.revokeObjectURL(state.previewPdfUrl);
        }
        state.previewPdfUrl = action.payload;
      })
      // Duplicate PDF
      .addCase(duplicateTemplatePdf.fulfilled, (state, action) => {
        state.templatesPdf.push(action.payload);
        state.message = 'Template duplicado com sucesso!';
        state.showMessage = true;
      })
      // Fetch Variables
      .addCase(fetchVariaveis.fulfilled, (state, action) => {
        state.variaveis = action.payload;
      });
  }
});

export const {
  setCurrentTemplate,
  clearCurrentTemplate,
  clearPreview,
  clearError,
  showTemplateMessage,
  hideTemplateMessage
} = templateSlice.actions;

export default templateSlice.reducer;
